// src/utils/redirectAfterAuth.js

/**
 * Durable "where to go after authenticating" intent.
 *
 * Problem this solves: when a logged-out user opens a shared link (e.g.
 * /n/:noteId), the intended destination used to be carried only in React
 * Router's in-memory `location.state.from`. That state is destroyed by (a) the
 * async auth-init path in AuthContext (a stale `traces:hasSession` triggers a
 * getCurrentUser() round-trip across several render cycles) and (b) the
 * apex->www 307 redirect, which can do a full document reload. Either way the
 * user landed on /home instead of the note on the first attempt.
 *
 * We instead persist the intent in sessionStorage:
 *  - per-tab, so a shared link in one tab never hijacks a normal login in
 *    another;
 *  - survives reloads and any number of render cycles (timing-independent);
 *  - auto-clears when the tab closes, and is consumed-once on successful auth.
 */
import { devWarn } from "./logger";

const REDIRECT_KEY = "traces:redirectAfterAuth";

// Max age for a stored intent. Guards against a destination written long ago
// (e.g. an abandoned tab) later hijacking a fresh login.
const MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes

// Auth pages must never be redirect targets, or we'd create a loop.
const AUTH_PREFIXES = [
  "/login",
  "/signup",
  "/verify-email",
  "/forgot-password",
  "/reset-password",
];

/**
 * Is `path` an internal, same-origin route we're willing to redirect to?
 * Accepts any path with a single leading slash, except auth pages.
 * Rejects protocol-relative (`//evil.com`) and backslash (`/\evil`) tricks.
 *
 * @param {unknown} path
 * @returns {boolean}
 */
export const isSafeRedirectPath = (path) => {
  if (typeof path !== "string" || path.length === 0) return false;
  if (path[0] !== "/") return false; // must be an internal absolute path
  if (path[1] === "/" || path[1] === "\\") return false; // no //host or /\host
  // Strip query/hash before comparing against auth-page prefixes.
  const pathnameOnly = path.split(/[?#]/)[0];
  return !AUTH_PREFIXES.some(
    (prefix) => pathnameOnly === prefix || pathnameOnly.startsWith(`${prefix}/`)
  );
};

/**
 * Store the destination to return to after the user authenticates.
 * No-op (with a dev warning) on an unsafe path or if storage is unavailable.
 *
 * @param {string} path - e.g. "/n/123?openComments=true"
 */
export const setRedirectAfterAuth = (path) => {
  if (!isSafeRedirectPath(path)) {
    devWarn("redirectAfterAuth: refusing to store unsafe path:", path);
    return;
  }
  try {
    window.sessionStorage.setItem(
      REDIRECT_KEY,
      JSON.stringify({ path, ts: Date.now() })
    );
  } catch (err) {
    // e.g. Safari private mode can throw on writes.
    devWarn("redirectAfterAuth: failed to store path:", err);
  }
};

/**
 * Read the stored destination without clearing it.
 * Returns null if absent, malformed, expired, or unsafe.
 *
 * @returns {string|null}
 */
export const getRedirectAfterAuth = () => {
  let raw;
  try {
    raw = window.sessionStorage.getItem(REDIRECT_KEY);
  } catch (err) {
    devWarn("redirectAfterAuth: failed to read path:", err);
    return null;
  }
  if (!raw) return null;

  try {
    const { path, ts } = JSON.parse(raw);
    if (typeof ts !== "number" || Date.now() - ts > MAX_AGE_MS) return null;
    if (!isSafeRedirectPath(path)) return null;
    return path;
  } catch (err) {
    devWarn("redirectAfterAuth: failed to parse stored path:", err);
    return null;
  }
};

/**
 * Remove any stored destination.
 */
export const clearRedirectAfterAuth = () => {
  try {
    window.sessionStorage.removeItem(REDIRECT_KEY);
  } catch (err) {
    devWarn("redirectAfterAuth: failed to clear path:", err);
  }
};

/**
 * Read the stored destination and clear it (consume-once).
 * Use this at auth-completion points; on success the intent is used up so it
 * can't hijack a later login.
 *
 * @returns {string|null}
 */
export const consumeRedirectAfterAuth = () => {
  const path = getRedirectAfterAuth();
  clearRedirectAfterAuth();
  return path;
};
